<template>
  <form @submit.prevent="submit" novalidate="true">
    <b-card>
      <div class="form-row mt-1">
        <div class="col-1 ml-auto" v-bind:class="{ inlineSpinner: isBusy }"></div>
      </div>

      <div v-if="notificationSettings">
        <b-form-group :label-cols-sm="2" :label="$t('Webportal.CheckReport')" label-class="font-weight-bold pt-0"
                      :disabled="isBusy">
          <b-form-checkbox v-model="notificationSettings.NotifyOnNewChecks"
                           v-validate="'required'" data-vv-name="NotifyOnNewChecks">
            {{ $t('Webportal.NotificationSettings.NotifyOnNewChecks') }}
          </b-form-checkbox>
          <b-form-checkbox v-model="notificationSettings.NotifyOnModifiedChecks"
                           v-validate="'required'" data-vv-name="NotifyOnModifiedChecks">
            {{ $t('Webportal.NotificationSettings.NotifyOnModifiedChecks') }}
          </b-form-checkbox>
        </b-form-group>

        <br/>
        <b-form-group :label-cols-sm="2" :label="$t('Webportal.Schedule')" label-class="font-weight-bold pt-0"
                      :disabled="isBusy || (!notificationSettings.NotifyOnNewChecks && !notificationSettings.NotifyOnModifiedChecks)">
          <b-form-group>
            <b-form-radio-group id="radio-schedule" v-model="notificationSettings.ScheduleType"
                                v-validate="'required'" name="radio-schedule" stacked>
              <b-form-radio value="Once">
                {{ $t('Webportal.NotificationSettings.OnceDaily') }}
                <dx-date-box v-model="notificationSettings.ScheduleTime" type="time"
                             class="custom-control-inline" width="6em" height="2em"
                             @valueChanged="scheduledTimeValueChanged($event)"
                             v-validate="'required'" data-vv-name="ScheduleTime" data-vv-validate-on="valueChanged" @input="void 0"
                             :use-mask-behavior="true" displayFormat="HH:mm"
                             :disabled="notificationSettings.ScheduleType != 'Once'" />
              </b-form-radio>
              <b-form-radio value="EveryHour" class="mt-2">
                {{ $t('Webportal.NotificationSettings.Every') }}
                <!-- Workaround: Empty input-event handler is needed or otherwise vee validate won't get called... -->
                <dx-number-box v-model="notificationSettings.ScheduleHours"
                               class="custom-control-inline" width="5em" height="2em"
                               v-validate="'required'" data-vv-name="ScheduleHours" @input="void 0"
                               format="#0" :show-spin-buttons="true" :step="1" :min="1" :max="23"
                               :disabled="notificationSettings.ScheduleType != 'EveryHour'" />
                {{ notificationSettings.ScheduleHours > 1 ? $t('Webportal.NotificationSettings.Hours') : $t('Webportal.NotificationSettings.Hour') }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </b-form-group>
      </div>

      <template slot="footer">
        <div class="float-right">
          <button @click.prevent="cancel" class="btn btn-secondary" :disabled="!isFormChanged || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
          </button>
          <button type="submit" class="btn btn-primary" :disabled="!isFormChanged || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t('Webportal.Save') }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import UsersService from '@/services/UsersService';
import { DxDateBox, DxNumberBox } from 'devextreme-vue';
import moment from "moment";

export default {
  name: "NotificationSettings",

  mixins: [ AppConfig ],

  components : {
    DxDateBox,
    DxNumberBox
  },


  data () {
    return {
      notificationSettings: null,

      isBusy: false
    }
  },

  computed: {
    isFormChanged() {
      return Object.keys(this.validationFields).some(key => this.validationFields[key].dirty || this.validationFields[key].changed);
    },
    isFormValid() {
      return Object.keys(this.validationFields).every(key => this.validationFields[key].valid === null || this.validationFields[key].valid);
    },

    notificationData: function() {
      let result = [];
      let cronScheduleUtc = '';

      let m = moment(this.notificationSettings.ScheduleTime).utc();

      if (this.notificationSettings.ScheduleType === "Once")
        cronScheduleUtc = `0 ${m.minutes()} ${m.hours()} ? * *`;
      else
        cronScheduleUtc = `0 0 0/${this.notificationSettings.ScheduleHours} ? * *`;

      let workflowEventIds = [];
      if (this.notificationSettings.NotifyOnNewChecks) workflowEventIds.push(1); // New check reports
      if (this.notificationSettings.NotifyOnModifiedChecks) workflowEventIds.push(2); // Modified check reports

      if (workflowEventIds.length > 0) {
        result.push(
          {
            Name: "Check Reports",
            NotificationTypeId: 1, // Mail
            CronScheduleUtc: cronScheduleUtc,
            Subscription: {
              WorkflowEventIds: workflowEventIds, 
            }
          }
        );
      }

      return result;
    },
  },

  activated() {
    this.getNotificationSettings();
  },

  beforeRouteLeave (to, from, next) {
    this.$emit('unsavedChanges', this.isFormChanged, next);
  },

  methods: {
    getNotificationSettings() {
      UsersService.getUserProfile()
        .then(response => {
          this.isBusy = false;

          const notificationData = response && response.data ? response.data.Notifications : null;

          this.notificationSettings = {
            NotifyOnNewChecks: false,
            NotifyOnModifiedChecks: false,
            ScheduleType: "Once",
            ScheduleTime: moment().hours(18).minutes(0).toDate(), // 18:00
            ScheduleHours: 4
          };

          if (notificationData && notificationData.length > 0)
          {
            const notification = notificationData[0];

            this.notificationSettings.NotifyOnNewChecks = notification.Subscription.WorkflowEventIds.includes(1);
            this.notificationSettings.NotifyOnModifiedChecks = notification.Subscription.WorkflowEventIds.includes(2);

            const cronParts = notification.CronScheduleUtc ? notification.CronScheduleUtc.split(' ') : [];
            if (cronParts.length >= 3) {
              const hourPart = cronParts[2];
              if (!hourPart.includes("/")) {
                this.notificationSettings.ScheduleType = "Once";
                this.notificationSettings.ScheduleTime = moment.utc().hours(Number(hourPart)).minutes(Number(cronParts[1])).local().toDate();
              }
              else {
                this.notificationSettings.ScheduleType = "EveryHour";
                this.notificationSettings.ScheduleHours = Number(hourPart.split('/')[1]);
              }
            };
          }

          this.$validator.reset();
        });
    },

    scheduledTimeValueChanged(event) {
      const scheduleTime = moment(this.notificationSettings.ScheduleTime);
      const remainder = scheduleTime.minutes() % 15; 

      if (remainder !== 0)
        this.notificationSettings.ScheduleTime = scheduleTime.add(15 - remainder, "minutes").toDate();
    },

    cancel() {
      this.$emit('unsavedChanges', this.isFormChanged, result => {
        if (result !== false) this.getNotificationSettings();
      });
    },

    submit() {
      this.isBusy = true;

      UsersService.setUserProfile({ Notifications: this.notificationData })
        .then(response => {
          this.isBusy = false;
           this.$validator.reset();
        })
        .catch(_ => { this.isBusy = false; } );
    }
  }
}
</script>
